import { Money, divideRoundedMinor, formatScaledDecimal } from '@klear/money'

export interface RateFormatOptions {
  digits?: number
}

export function rateToJson(rate: Rate) {
  return {
    numerator: rate.numerator.toString(),
    denominator: rate.denominator.toString()
  }
}
export type RateJson = ReturnType<typeof rateToJson>

function gcd(a: bigint, b: bigint): bigint {
  let x = a < 0n ? -a : a
  let y = b < 0n ? -b : b

  while (y !== 0n) {
    const next = x % y
    x = y
    y = next
  }

  return x
}

export function normalizeRational(numerator: bigint, denominator: bigint): { numerator: bigint; denominator: bigint } {
  if (denominator === 0n) throw new Error('Rate denominator cannot be zero')
  if (denominator < 0n) {
    numerator = -numerator
    denominator = -denominator
  }

  const divisor = gcd(numerator, denominator)
  if (divisor === 0n) return { numerator: 0n, denominator: 1n }
  return { numerator: numerator / divisor, denominator: denominator / divisor }
}

export class Rate {
  static readonly __klearClass = 'Rate'
  static readonly ZERO = Rate.of(0n, 1n)

  readonly numerator!: bigint
  readonly denominator!: bigint

  constructor(other: Rate | RateJson) {
    if (other instanceof Rate) {
      this.numerator = other.numerator
      this.denominator = other.denominator
      return
    }
    this.numerator = BigInt(other.numerator)
    this.denominator = BigInt(other.denominator)
  }

  static of(numerator: bigint, denominator: bigint): Rate {
    const reduced = normalizeRational(numerator, denominator)
    return new Rate({ numerator: reduced.numerator.toString(), denominator: reduced.denominator.toString() })
  }

  scaled(times: bigint, over: bigint): Rate {
    if (over === 0n) throw new Error('Rate cannot be scaled over zero')
    return Rate.of(this.numerator * times, this.denominator * over)
  }

  applyTo(money: Money): Money {
    return Money.fromMinorUnits(divideRoundedMinor(money.amount * this.numerator, this.denominator), money.currency)
  }

  toString(options: RateFormatOptions = {}): string {
    const digits = options.digits ?? 2
    return `${this.toPercentString(digits)}%`
  }

  toPercentString(digits: number = 2): string {
    const scale = 10n ** BigInt(digits)
    return formatScaledDecimal(divideRoundedMinor(this.numerator * 100n * scale, this.denominator), digits)
  }

  toJSON(): RateJson {
    return rateToJson(this)
  }
}
